/**
 * useSignalR — React hook around the per-instance push channel.
 *
 * Lifted from the Outlook add-in's `useSignalR`.
 * Changes from the add-in original:
 *   - Accepts `getPushChannel: GetPushChannelFn` as a parameter instead of
 *     importing orchestratorApi, enabling both apps to share this hook.
 *   - `logger` replaced with plain `console.*` to avoid cross-app logger deps.
 *   - Optional `onReconnected` callback (Phase 2.5a) — consumers register a
 *     post-reconnect refetch so missed events are reconciled. The substrate
 *     does NOT replay `workflowEvent`s that fired during a transient WS
 *     outage; without this hook, every Wi-Fi handoff or token-TTL expiry
 *     leaves the drawer stuck on stale state until manual refresh.
 *
 * Usage:
 *   const { isConnected, isSignalRUnavailable, connect, disconnect, subscribe }
 *     = useSignalR(myGetPushChannelFn);
 *
 *   // Or with reconnect reconciliation:
 *   useSignalR(myGetPushChannelFn, {
 *     onReconnected: (instanceId) => void refetchFullStatus(instanceId),
 *   });
 *
 *   await connect(instanceId);
 *   const off = subscribe(WORKFLOW_EVENT_METHOD, (evt: WorkflowEvent) => { ... });
 *   off();
 *   await disconnect(instanceId);
 *
 * Import paths:
 *   ora    → ~/common/workflows/signalr/useSignalR
 *   add-in → @product-app/common/workflows/signalr/useSignalR
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { HubConnection } from "@microsoft/signalr";
import {
  SIGNALR_UNAVAILABLE,
  SIGNALR_COOLDOWN,
  createSignalRConnection,
  stopConnection,
  type GetPushChannelFn,
  type SignalRConnectedInfo,
} from "./signalrClient";

export { SIGNALR_METHOD, WORKFLOW_EVENT_METHOD, WF_EVENT_METHOD } from "./signalrClient";
export type { GetPushChannelFn, SignalRConnectedInfo } from "./signalrClient";

type AnyHandler = (payload: unknown) => void;

// Module-level — once the substrate signals unavailability, all future hook
// mounts short-circuit without retrying so we don't spam the negotiate endpoint.
let _signalrUnavailable = false;

// Per-instance count of consecutive non-sentinel connect failures (e.g. a WS
// handshake that throws ConnectionDoesNotExistError, or any start() error that
// is NOT a clean 503). A clean 503 trips `_signalrUnavailable` immediately; but
// a connection that simply never holds used to leave the UI stuck in the
// "connecting" state forever (isConnected=false, isSignalRUnavailable=false),
// so the backstop polling never downgraded and the client kept re-minting
// tokens. After this many consecutive failures we give up and fall back to
// polling, exactly like a 503. Reset on a successful connect.
const _failCounts = new Map<string, number>();
const _UNAVAILABLE_AFTER_FAILURES = 3;

/** `map[key] += by`, dropping the key at zero. */
function bump(map: Map<string, number>, key: string, by: number): void {
  const next = (map.get(key) ?? 0) + by;
  if (next > 0) map.set(key, next);
  else map.delete(key);
}

export function _resetUseSignalRForTests(): void {
  _signalrUnavailable = false;
  _failCounts.clear();
}

/**
 * Optional configuration for `useSignalR`. All fields are optional — passing
 * an empty options object (or omitting it) preserves the legacy behavior.
 */
export interface UseSignalROptions {
  /**
   * Called after `@microsoft/signalr` re-establishes the WebSocket
   * following a transient drop. The instanceId argument is the
   * connection that recovered. Consumers should refetch their full
   * state envelope here — the substrate does NOT replay
   * `workflowEvent`s that fired during the outage.
   *
   * The callback is held by ref, so changing it across renders does
   * not force the underlying SignalR handlers to be re-registered.
   * Errors in the callback are caught and logged so a misbehaving
   * consumer can't poison the connection.
   */
  onReconnected?: (instanceId: string) => void;
  /**
   * Called once when a new WebSocket connection is established for an
   * instance. Consumers use this to join the SignalR group on the local
   * emulator, which does not honour JWT `groups` claims automatically.
   * No-op in production where the real Azure SignalR Service handles
   * group membership from the token.
   */
  onConnected?: (info: SignalRConnectedInfo & { instanceId: string }) => void;
}

export interface UseSignalRValue {
  isConnected: boolean;
  isSignalRUnavailable: boolean;
  connect: (instanceId: string) => Promise<void>;
  disconnect: (instanceId?: string) => Promise<void>;
  subscribe: <T = unknown>(eventName: string, handler: (payload: T) => void) => () => void;
}

export function useSignalR(
  getPushChannel: GetPushChannelFn,
  options: UseSignalROptions = {},
): UseSignalRValue {
  /** The ONE module reference this hook holds per key (signalrClient's refcount). */
  const connectionsRef = useRef<Map<string, HubConnection>>(new Map());
  const handlersRef = useRef<Map<string, Set<AnyHandler>>>(new Map());
  // Story 29533. Acquires in flight per key, and how many of them a disconnect has
  // already released: a cleanup that runs mid-connect (StrictMode's mount-unmount-
  // mount, a quick unmount) must not release a reference before it is taken. Doing
  // so wiped the shared connect-in-progress marker, so the next mount opened a
  // SECOND connection that never joined the user group, and a hook attached to it
  // heard nothing.
  const pendingRef = useRef<Map<string, number>>(new Map());
  const cancelledRef = useRef<Map<string, number>>(new Map());
  /** The events this hook's handlers are on, per connection: each wired once. */
  const wiredRef = useRef<WeakMap<HubConnection, Set<string>>>(new WeakMap());
  /** The connections this hook's lifecycle callbacks are on. */
  const watchedRef = useRef<WeakSet<HubConnection>>(new WeakSet());
  const [isConnected, setIsConnected] = useState(false);
  const [isSignalRUnavailableState, setIsSignalRUnavailableState] =
    useState(_signalrUnavailable);

  // Hold callbacks by ref so the consumer can change them across renders
  // without forcing this hook's `connect` callback to be regenerated (which
  // would otherwise destabilize useEffect deps in every consumer that
  // includes signalR.connect in its dep array).
  const onReconnectedRef = useRef<UseSignalROptions["onReconnected"]>(
    options.onReconnected,
  );
  onReconnectedRef.current = options.onReconnected;

  const onConnectedRef = useRef<UseSignalROptions["onConnected"]>(
    options.onConnected,
  );
  onConnectedRef.current = options.onConnected;

  const wireEvent = useCallback((conn: HubConnection, eventName: string) => {
    let wired = wiredRef.current.get(conn);
    if (!wired) {
      wired = new Set();
      wiredRef.current.set(conn, wired);
    }
    if (wired.has(eventName)) return;
    wired.add(eventName);
    conn.on(eventName, (payload: unknown) => {
      handlersRef.current.get(eventName)?.forEach((fn) => fn(payload));
    });
  }, []);

  const wireHandlers = useCallback(
    (conn: HubConnection) => {
      handlersRef.current.forEach((_set, eventName) => wireEvent(conn, eventName));
    },
    [wireEvent],
  );

  const connect = useCallback(
    async (instanceId: string) => {
      if (!instanceId || _signalrUnavailable) return;
      if (connectionsRef.current.has(instanceId)) return;
      bump(pendingRef.current, instanceId, 1);
      try {
        const onConnectedCallback = onConnectedRef.current;
        const conn = await createSignalRConnection(
          instanceId,
          getPushChannel,
          onConnectedCallback
            ? (info) => onConnectedCallback({ instanceId, ...info })
            : undefined,
        );
        bump(pendingRef.current, instanceId, -1);
        if ((cancelledRef.current.get(instanceId) ?? 0) > 0) {
          // Disconnected while in flight: give back the reference it just took.
          bump(cancelledRef.current, instanceId, -1);
          await stopConnection(instanceId);
          return;
        }
        if (connectionsRef.current.has(instanceId)) {
          // A second acquire in flight landed second: the hook holds one reference.
          await stopConnection(instanceId);
          return;
        }
        connectionsRef.current.set(instanceId, conn);
        wireHandlers(conn);
        setIsConnected(true);
        _failCounts.delete(instanceId);
        console.info("[useSignalR] connected", { instance_id: instanceId });
        if (watchedRef.current.has(conn)) return;
        watchedRef.current.add(conn);
        conn.onreconnecting(() => setIsConnected(false));
        conn.onreconnected(() => {
          setIsConnected(true);
          // Phase 2.5a: WebSocket recovered after a transient drop. Fire
          // the consumer's reconcile callback so missed events get
          // refetched. Wrapped in try/catch so a buggy consumer can't
          // bring down the connection.
          try {
            onReconnectedRef.current?.(instanceId);
          } catch (err) {
            console.error("[useSignalR] onReconnected handler threw", {
              instance_id: instanceId,
              error: err instanceof Error ? err.message : String(err),
            });
          }
        });
        conn.onclose(() => setIsConnected(false));
      } catch (err) {
        bump(pendingRef.current, instanceId, -1);
        // A disconnect that released this acquire mid-flight has nothing to give back.
        if ((cancelledRef.current.get(instanceId) ?? 0) > (pendingRef.current.get(instanceId) ?? 0)) {
          bump(cancelledRef.current, instanceId, -1);
        }
        const msg = err instanceof Error ? err.message : String(err);
        if (msg === SIGNALR_UNAVAILABLE) {
          _signalrUnavailable = true;
          setIsSignalRUnavailableState(true);
          console.info("[useSignalR] SignalR unavailable — falling back to polling");
        } else if (msg === SIGNALR_COOLDOWN) {
          // Within the per-instance connect backoff window — the underlying
          // failure was already counted on the attempt that opened the
          // cooldown. Stay quiet; the consumer keeps polling.
        } else {
          const failures = (_failCounts.get(instanceId) ?? 0) + 1;
          _failCounts.set(instanceId, failures);
          if (failures >= _UNAVAILABLE_AFTER_FAILURES) {
            // Persistent connect failures that never resolve to a clean 503
            // (e.g. a WS that negotiates but won't hold). Give up so every
            // consumer downgrades to polling instead of looping on negotiate.
            _signalrUnavailable = true;
            setIsSignalRUnavailableState(true);
            console.warn(
              "[useSignalR] repeated connect failures — falling back to polling",
              { instance_id: instanceId, failures, error: msg },
            );
          } else {
            console.error("[useSignalR] connect failed", {
              instance_id: instanceId,
              error: msg,
              failures,
            });
          }
        }
        setIsConnected(false);
      }
    },
    [getPushChannel, wireHandlers],
  );

  /** Mark every acquire still in flight as released: each gives its reference back on landing. */
  const cancelPending = useCallback((instanceId?: string) => {
    pendingRef.current.forEach((count, id) => {
      if (instanceId === undefined || id === instanceId) cancelledRef.current.set(id, count);
    });
  }, []);

  const disconnect = useCallback(
    async (instanceId?: string) => {
      if (instanceId) {
        if (connectionsRef.current.has(instanceId)) {
          connectionsRef.current.delete(instanceId);
          await stopConnection(instanceId);
          if (connectionsRef.current.size === 0) setIsConnected(false);
          return;
        }
        // Still connecting: release that acquire when it lands, never one not yet taken.
        cancelPending(instanceId);
        return;
      }
      const ids = Array.from(connectionsRef.current.keys());
      connectionsRef.current.clear();
      cancelPending();
      await Promise.all(ids.map(stopConnection));
      setIsConnected(false);
    },
    [cancelPending],
  );

  const subscribe = useCallback(
    <T = unknown,>(eventName: string, handler: (payload: T) => void) => {
      const wrapped: AnyHandler = (payload) => handler(payload as T);
      let set = handlersRef.current.get(eventName);
      if (!set) {
        set = new Set();
        handlersRef.current.set(eventName, set);
        connectionsRef.current.forEach((conn) => wireEvent(conn, eventName));
      }
      set.add(wrapped);
      return () => {
        handlersRef.current.get(eventName)?.delete(wrapped);
      };
    },
    [wireEvent],
  );

  useEffect(
    () => () => {
      const ids = Array.from(connectionsRef.current.keys());
      connectionsRef.current.clear();
      handlersRef.current.clear();
      cancelPending();
      ids.forEach((id) => void stopConnection(id));
    },
    [cancelPending],
  );

  return {
    isConnected,
    isSignalRUnavailable: isSignalRUnavailableState,
    connect,
    disconnect,
    subscribe,
  };
}
